import cx from "classnames";
import dayjs from "dayjs";
import { t } from "ttag";

import { getCurrentVersion } from "metabase/admin/app/selectors";
import { useGetVersionInfoQuery } from "metabase/api";
import { ExternalLink } from "metabase/common/components/ExternalLink";
import { useEolDate } from "metabase/common/hooks";
import ButtonsS from "metabase/css/components/buttons.module.css";
import CS from "metabase/css/core/index.css";
import { useSelector } from "metabase/redux";
import { Anchor, Flex, HoverCard, Icon, Stack, Tabs, Text } from "metabase/ui";
import { getMajorVersion, newVersionAvailable } from "metabase/utils/version";

import S from "./VersionUpdateNotice.module.css";
import { getEolMessage, getVersionMessage } from "./utils";

const embedQueryParams = "?hide_nav=true&no_gdpr=true";

export function VersionUpdateNotice() {
  const { data: versionInfo } = useGetVersionInfoQuery();
  const currentVersion = useSelector(getCurrentVersion);
  const latestVersion = versionInfo?.latest?.version;
  const { eolDate, isEol } = useEolDate();

  return (
    <VersionMessage
      currentVersion={currentVersion}
      latestVersion={latestVersion}
      eolDate={eolDate}
      isEol={isEol}
    />
  );
}

interface VersionMessageProps {
  currentVersion: string;
  latestVersion?: string;
  eolDate?: Date | null;
  isEol: boolean;
}

function VersionMessage({
  currentVersion,
  latestVersion,
  eolDate,
  isEol,
}: VersionMessageProps) {
  const isNewVersionAvailable =
    latestVersion != null &&
    newVersionAvailable({ currentVersion, latestVersion });
  const message = getVersionMessage(currentVersion, latestVersion, isEol);
  const textColor = isEol ? "text-primary" : "text-primary-inverse";
  return (
    <Flex
      align="center"
      justify="space-between"
      p="lg"
      bg={isEol ? "background_surface-warning" : "core-brand"}
      bd={isEol ? "1px solid var(--mb-color-feedback-warning)" : undefined}
      bdrs="sm"
    >
      <Text fw="bold" c={textColor} miw={0}>
        {message}
        {isNewVersionAvailable && eolDate && (
          <EolHoverCard
            currentVersion={currentVersion}
            eolDate={eolDate}
            isEol={isEol}
          >
            <Icon name="info" c={textColor} className={S.inlineInfoIcon} />
          </EolHoverCard>
        )}
      </Text>
      {isNewVersionAvailable && (
        <ExternalLink
          className={cx(
            ButtonsS.Button,
            ButtonsS.ButtonWhite,
            ButtonsS.ButtonMedium,
            CS.borderless,
          )}
          href="https://www.metabase.com/docs/latest/installation-and-operation/upgrading-metabase"
        >
          {t`Update`}
        </ExternalLink>
      )}
      {!isNewVersionAvailable && eolDate && (
        <EolHoverCard
          currentVersion={currentVersion}
          eolDate={eolDate}
          isEol={isEol}
        >
          <Flex
            align="center"
            gap="xs"
            p="xs"
            bdrs="sm"
            className={S.hoverCardTrigger}
          >
            <Icon name="heart_handshake" c={textColor} />
            <Text c={textColor} fw="bold">
              {dayjs(eolDate).utc().format("ll")}
            </Text>
          </Flex>
        </EolHoverCard>
      )}
    </Flex>
  );
}

interface EolHoverCardProps {
  currentVersion: string;
  eolDate: Date;
  isEol: boolean;
  children: React.ReactNode;
}

function EolHoverCard({
  currentVersion,
  eolDate,
  isEol,
  children,
}: EolHoverCardProps) {
  const eolMessage = getEolMessage(currentVersion, eolDate, isEol);
  return (
    <HoverCard>
      <HoverCard.Target>{children}</HoverCard.Target>
      <HoverCard.Dropdown>
        <Stack gap="sm" p="md" w="18rem">
          <Text>{eolMessage}</Text>
          <Anchor
            component={ExternalLink}
            href="https://www.metabase.com/version-support"
            fw="bold"
          >
            {t`Learn more`}
          </Anchor>
        </Stack>
      </HoverCard.Dropdown>
    </HoverCard>
  );
}

export function NewVersionInfo() {
  const { data: versionInfo } = useGetVersionInfoQuery();
  const latestMajorVersion =
    getMajorVersion(versionInfo?.latest?.version ?? "") ?? "";

  return (
    <Tabs mt="md" defaultValue="whats-new">
      <Tabs.List>
        <Tabs.Tab value="whats-new">{t`What's new`}</Tabs.Tab>
        <Tabs.Tab value="changelog">{t`Changelog`}</Tabs.Tab>
      </Tabs.List>
      <Tabs.Panel value="whats-new">
        <iframe
          data-testid="releases-iframe"
          src={`https://www.metabase.com/releases${embedQueryParams}`}
          className={S.iframe}
        />
      </Tabs.Panel>
      <Tabs.Panel value="changelog">
        <iframe
          data-testid="changelog-iframe"
          src={`https://www.metabase.com/changelog/${latestMajorVersion}${embedQueryParams}`}
          className={S.iframe}
        />
      </Tabs.Panel>
    </Tabs>
  );
}
